import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../../ui/button';
import {
  X, RefreshCw, CheckCircle, Banknote, CreditCard, Download, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { EmailTicketForm } from '../../EmailTicketForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build a client-facing URL (never the backend host) so the QR opens on
// pont10.cat in production, on the preview domain in dev.
const buildTicketUrl = (token) => `${window.location.origin}/t/${token}`;

const ReceiptTicketModal = ({ saleData, onClose }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!saleData?.sale_id) return;
    const jwt = localStorage.getItem('admin_token');
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ticket-public/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
          body: JSON.stringify({ sale_id: saleData.sale_id }),
        });
        if (!res.ok) throw new Error('No se pudo generar el enlace del ticket');
        const j = await res.json();
        setToken(j.token);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [saleData?.sale_id]);

  if (!saleData) return null;

  const { mesa_numero, total, items, payment_method, closed_at } = saleData;
  const paymentLabel = payment_method === 'efectivo' ? 'Efectivo' : 'Tarjeta';
  const PayIcon = payment_method === 'efectivo' ? Banknote : CreditCard;

  let dateStr = '', timeStr = '';
  try {
    const dt = new Date(closed_at);
    dateStr = dt.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    timeStr = dt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    dateStr = new Date().toLocaleDateString('es-ES');
    timeStr = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }

  const handleDownloadPdf = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/ticket-public/${token}/pdf`);
      if (!res.ok) throw new Error('PDF no disponible');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-pont10-${token.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const ticketUrl = token ? buildTicketUrl(token) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid="receipt-ticket-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-900 text-white px-6 py-4 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid="receipt-close-btn"
          >
            <X size={16} className="text-white" />
          </button>
          <h1 className="text-2xl font-bold tracking-wider">PONT 10</h1>
          <p className="text-gray-400 text-xs mt-1">Ticket entregado</p>
        </div>

        {/* Meta */}
        <div className="px-5 pt-3 pb-1 flex items-center justify-between">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Mesa</p>
            <p className="text-xl font-bold">{mesa_numero ?? '-'}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-400">{dateStr}</p>
            <p className="text-sm font-medium">{timeStr}</p>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-auto px-5 pb-1">
          <div className="border-t-2 border-gray-900 pt-2">
            <div className="grid grid-cols-12 gap-1 mb-1">
              <span className="col-span-7 text-[10px] text-gray-400 uppercase font-medium">Producto</span>
              <span className="col-span-2 text-[10px] text-gray-400 uppercase font-medium text-center">Uds</span>
              <span className="col-span-3 text-[10px] text-gray-400 uppercase font-medium text-right">Importe</span>
            </div>
            {(items || []).map((item, idx) => {
              let name = item.product_name || item.name || 'Producto';
              if (typeof name === 'object') name = name.es || Object.values(name)[0] || 'Producto';
              const qty = item.quantity || 1;
              const price = item.price || 0;
              const overridden = item.price_overridden && item.original_price != null
                && Number(item.original_price) !== Number(item.price);
              return (
                <div key={idx} className="grid grid-cols-12 gap-1 py-1.5 border-b border-gray-100">
                  <div className="col-span-7">
                    <p className="text-sm font-medium text-gray-800 leading-tight">{name}</p>
                    {item.notes && <p className="text-[10px] text-gray-400 mt-0.5">{item.notes}</p>}
                    {overridden && (
                      <p className="text-[10px] text-amber-600 mt-0.5">
                        Precio ajustado · <span className="line-through">{Number(item.original_price).toFixed(2)}€</span> → {Number(price).toFixed(2)}€
                      </p>
                    )}
                  </div>
                  <p className="col-span-2 text-sm text-gray-500 text-center">{qty}</p>
                  <p className="col-span-3 text-sm font-semibold text-right">{(qty * price).toFixed(2)}€</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Total + Pago */}
        <div className="px-5 py-2">
          <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase font-semibold">Total</span>
            <span className="text-2xl font-extrabold text-green-600">{total?.toFixed(2)}€</span>
          </div>
          <div className="flex justify-center mt-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
              payment_method === 'efectivo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <PayIcon size={13} />
              {paymentLabel}
            </span>
          </div>
        </div>

        <div className="mx-5 border-t border-dashed border-gray-300" />

        {/* QR Section - the main event */}
        <div className="px-5 py-4 flex flex-col items-center gap-3 bg-gray-50">
          {loading && (
            <div className="flex items-center gap-2 text-gray-400 py-8">
              <RefreshCw size={16} className="animate-spin" />
              <span className="text-sm">Generando QR...</span>
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 py-4 text-center">{error}</div>
          )}
          {ticketUrl && (
            <>
              <div className="bg-white p-3 rounded-xl border-2 border-gray-900" data-testid="ticket-qr">
                <QRCodeSVG value={ticketUrl} size={180} level="M" />
              </div>
              <div className="flex items-center gap-2 text-gray-900">
                <QrCode size={16} />
                <p className="text-sm font-semibold">Escanea para tu ticket</p>
              </div>
              <p className="text-[11px] text-gray-500 text-center leading-snug">
                Válido 30 días · descargable en PDF
              </p>
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                className="w-full h-10 flex items-center gap-2"
                data-testid="receipt-download-pdf-btn"
              >
                <Download size={14} />
                Descargar PDF ahora
              </Button>
              <EmailTicketForm token={token} label="O envíalo al correo del cliente" idPrefix="receipt" />
            </>
          )}
        </div>

        {/* Close */}
        <div className="px-5 py-3">
          <Button
            onClick={onClose}
            className="w-full h-11 bg-gray-900 hover:bg-gray-800"
            data-testid="receipt-done-btn"
          >
            <CheckCircle size={16} className="mr-2" />
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptTicketModal;
